import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Smile } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// avg_score 1-5 → color (1=bad red, 5=great green)
const MOOD_BG = (score) => {
    if (score == null) return 'bg-muted/30';
    if (score >= 4.5) return 'bg-emerald-500 text-white';
    if (score >= 3.5) return 'bg-emerald-300';
    if (score >= 2.5) return 'bg-amber-300';
    if (score >= 1.5) return 'bg-orange-400 text-white';
    return 'bg-rose-500 text-white';
};

const MOOD_EMOJI = { great: '😄', good: '🙂', okay: '😐', low: '😕', bad: '😢' };

export default function MoodCalendar() {
    const { language } = useLanguage();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(90);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await axios.get(`${API}/api/mood/calendar?days=${days}`);
                setData(res.data);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, [days]);

    if (loading) return <Card className="animate-pulse"><CardContent className="h-40" /></Card>;
    if (!data) return null;

    const today = new Date(); today.setHours(0, 0, 0, 0);
    const map = {}; (data.by_date || []).forEach(d => { map[d.date] = d; });
    const cells = [];
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today); d.setDate(d.getDate() - i);
        const ds = d.toISOString().slice(0, 10);
        cells.push({ date: ds, ...(map[ds] || { count: 0, avg_score: null, moods: [] }) });
    }
    const firstWeekday = (new Date(cells[0].date).getDay() + 6) % 7;
    const padded = Array(firstWeekday).fill(null).concat(cells);
    const weeks = [];
    for (let i = 0; i < padded.length; i += 7) weeks.push(padded.slice(i, i + 7));

    const t = (de, en) => language === 'de' ? de : en;

    return (
        <Card data-testid="mood-calendar">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                        <Smile className="w-4 h-4 text-emerald-500" />
                        {t('Stimmungs-Kalender', 'Mood calendar')}
                    </span>
                    <select
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value))}
                        className="text-xs h-7 px-2 rounded-md border border-input bg-background"
                        data-testid="mood-calendar-range"
                    >
                        <option value={30}>30 {t('Tage', 'days')}</option>
                        <option value={90}>90 {t('Tage', 'days')}</option>
                        <option value={180}>180 {t('Tage', 'days')}</option>
                    </select>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                        <p className="text-lg font-bold text-emerald-600">{data.total_entries}</p>
                        <p className="text-[10px] text-muted-foreground">{t('Einträge', 'entries')}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/30">
                        <p className="text-lg font-bold text-blue-600">{data.days_tracked}</p>
                        <p className="text-[10px] text-muted-foreground">{t('Tage getrackt', 'days tracked')}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30">
                        <p className="text-lg font-bold text-amber-600">
                            {data.distribution?.[0]?.mood ? MOOD_EMOJI[data.distribution[0].mood] || '—' : '—'}
                        </p>
                        <p className="text-[10px] text-muted-foreground">{t('Häufigste', 'Most frequent')}</p>
                    </div>
                </div>

                <div className="flex gap-1 overflow-x-auto pb-1">
                    {weeks.map((week, wi) => (
                        <div key={wi} className="flex flex-col gap-1">
                            {Array(7).fill(null).map((_, di) => {
                                const cell = week[di];
                                if (!cell) return <div key={di} className="w-3.5 h-3.5" />;
                                const cls = MOOD_BG(cell.avg_score);
                                return (
                                    <div
                                        key={di}
                                        className={`w-3.5 h-3.5 rounded-sm cursor-pointer ${cls} hover:ring-2 hover:ring-primary transition`}
                                        title={`${cell.date}${cell.count ? `: ${cell.count}× Ø ${cell.avg_score}/5` : ''}`}
                                        onMouseEnter={() => cell.count > 0 && setHover(cell)}
                                        onClick={() => cell.count > 0 && setHover(cell)}
                                        data-testid={`mood-day-${cell.date}`}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>

                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                    <span>{t('Schlecht', 'Bad')}</span>
                    <div className="w-3 h-3 rounded-sm bg-rose-500" />
                    <div className="w-3 h-3 rounded-sm bg-orange-400" />
                    <div className="w-3 h-3 rounded-sm bg-amber-300" />
                    <div className="w-3 h-3 rounded-sm bg-emerald-300" />
                    <div className="w-3 h-3 rounded-sm bg-emerald-500" />
                    <span>{t('Top', 'Great')}</span>
                </div>

                {hover && (
                    <div className="p-3 rounded-lg bg-muted/40 border border-border" data-testid="mood-detail">
                        <p className="text-xs font-semibold mb-1">
                            {new Date(hover.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                            <span className="ml-2 text-muted-foreground">Ø {hover.avg_score}/5</span>
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {hover.moods.map((m, i) => (
                                <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-background border">
                                    {MOOD_EMOJI[m.mood] || ''} {m.mood}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {data.distribution?.length > 0 && (
                    <div className="pt-2 border-t border-border">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">
                            {t('Verteilung', 'Distribution')}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {data.distribution.map(m => (
                                <span key={m.mood} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900">
                                    {MOOD_EMOJI[m.mood] || ''} {m.mood} × {m.count}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
